import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function VendorRegister() {
  const { t, L } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    vendor_name_zh: "", vendor_name_en: "", category: "", bio_zh: "", contact_phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/vendor-register", form);
      setUser(data.user);
      toast.success(L("摊主账号已提交，等待管理员批准", "Vendor account submitted — awaiting admin approval"));
      navigate("/vendor");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const field = "fk-border rounded-lg mt-1";

  return (
    <div className="max-w-2xl mx-auto px-4 py-14">
      <div className="bg-white fk-border-thick rounded-2xl p-6 sm:p-8 fk-shadow">
        <div className="font-mono-fk text-xs tracking-widest text-[#FF5757] mb-2">VENDOR SIGN UP · 摊位注册</div>
        <h1 className="font-display text-4xl flex items-center gap-2"><Store size={32} /> {t("join_market")}</h1>
        <p className="text-black/60 text-sm mt-2">{L("注册后需管理员批准，批准后即可管理你的摊主专页。", "After registration, an admin approves your account. Then you can manage your vendor page.")}</p>

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">{t("name")} *</label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vr-name" className={field} />
            </div>
            <div>
              <label className="text-sm font-medium">{t("email")} *</label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vr-email" className={field} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Password *</label>
            <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="vr-password" className={field} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">{L("摊位名称（中）", "Stall name (ZH)")} *</label>
              <Input required value={form.vendor_name_zh} onChange={(e) => setForm({ ...form, vendor_name_zh: e.target.value })} data-testid="vr-name-zh" className={field} />
            </div>
            <div>
              <label className="text-sm font-medium">{L("摊位名称（英）", "Stall name (EN)")}</label>
              <Input value={form.vendor_name_en} onChange={(e) => setForm({ ...form, vendor_name_en: e.target.value })} data-testid="vr-name-en" className={field} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">{L("类别", "Category")} *</label>
              <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                <SelectTrigger className={field} data-testid="vr-category"><SelectValue placeholder={L("选择类别", "Select category")} /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c.id} value={c.name_zh}>{L(c.name_zh, c.name_en)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t("phone")}</label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="vr-phone" className={field} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">{L("摊位简介", "Stall bio")}</label>
            <Textarea rows={3} value={form.bio_zh} onChange={(e) => setForm({ ...form, bio_zh: e.target.value })} data-testid="vr-bio" className={field} />
          </div>
          <button disabled={loading} data-testid="vr-submit" className="w-full fk-border rounded-full px-6 py-3 font-semibold bg-[#FFD93D] fk-lift disabled:opacity-50">
            {loading ? "…" : t("register")}
          </button>
        </form>
        <p className="text-sm text-center mt-4 text-black/60">
          {L("已有账号？", "Have an account?")} <Link to="/login" className="underline font-semibold">{t("login")}</Link>
        </p>
      </div>
    </div>
  );
}
